// Hook für Bottom-Sheet drag-down-to-close auf Mobile.
//
// Nutzung: an einem "handle"-Element (z.B. dem Header eines Sheets) die
// returnte `handlers`-Props anhängen + `dragY` als translateY/opacity auf
// das Sheet anwenden. Bei loslassen > THRESHOLD wird `onClose` aufgerufen,
// sonst snappt das Sheet zurück.
//
// Nur Touch — Mouse-Drag wäre auf Desktop redundant (Backdrop-Click reicht).
import { useEffect, useRef, useState } from 'react'

const CLOSE_THRESHOLD_PX = 100
const SETTLE_MS = 200

export function useDragToClose(onClose: () => void, enabled: boolean) {
  const startYRef = useRef<number | null>(null)
  const [dragY, setDragY] = useState(0)
  const [settling, setSettling] = useState(false)

  // Reset wenn enabled wechselt
  useEffect(() => {
    if (!enabled) {
      setDragY(0)
      setSettling(false)
      startYRef.current = null
    }
  }, [enabled])

  if (!enabled) {
    return {
      dragY: 0,
      handlers: {},
      sheetStyle: undefined,
    }
  }

  const handlers = {
    onTouchStart: (e: React.TouchEvent) => {
      if (e.touches.length !== 1) return
      startYRef.current = e.touches[0].clientY
      setSettling(false)
    },
    onTouchMove: (e: React.TouchEvent) => {
      if (startYRef.current == null) return
      const dy = e.touches[0].clientY - startYRef.current
      // Nur nach unten ziehen — negative Werte (= nach oben) ignorieren.
      setDragY(Math.max(0, dy))
    },
    onTouchEnd: () => {
      const dy = dragY
      startYRef.current = null
      if (dy > CLOSE_THRESHOLD_PX) {
        // Snap nach unten weiterspielen und dann schließen.
        setSettling(true)
        setDragY(window.innerHeight)
        setTimeout(onClose, SETTLE_MS)
      } else {
        // Snap zurück nach oben.
        setSettling(true)
        setDragY(0)
        setTimeout(() => setSettling(false), SETTLE_MS)
      }
    },
  }

  return {
    dragY,
    handlers,
    sheetStyle: {
      transform: `translateY(${dragY}px)`,
      transition: settling ? `transform ${SETTLE_MS}ms ease-out` : undefined,
      // Backdrop fadet während des Ziehens — gibt visuelles Feedback.
      opacity: dragY > 0 ? Math.max(0.3, 1 - dragY / 400) : 1,
    } satisfies React.CSSProperties,
  }
}
